import { Box, Stack } from "@mui/material";
import { Avatar } from "@src/appV2/lib/Avatar";
import { Button } from "@src/appV2/redesign/components/Button";
import { Dropdown } from "antd";

import { Icon } from "@src/appV2/redesign/components/Icon";
import { Text } from "@src/appV2/redesign/components/Text";
import { AccountSwitcherDropdownAdmin } from "./AccountSwitcherDropdownAdmin";
import { AccountSwitcherMenuWorkplace } from "./AccountSwitcherDropdownWorkplace";
import { useAccountSwitcherHook } from "./useAccountSwitcherHook";

interface AccountSwitcherProps {
  adminUserId: string;
  pseudo: (data: { userId: string }, isFirebaseSession?: boolean) => void;
  closeMenu?: () => void;
  isAdminUser: boolean;
  isFacilityUser: boolean;
  facilityName?: string;
  userName?: string;
  adminName?: string;
}

export function AccountSwitcher(props: AccountSwitcherProps) {
  const {
    adminUserId,
    pseudo,
    closeMenu,
    isAdminUser,
    isFacilityUser,
    facilityName,
    userName,
    adminName,
  } = props;

  const {
    handleChange,
    agentDetails,
    workplaceDetails,
    handleOnDropdownVisible,
    accountsListVisible,
    setAccountsListVisible,
    searchAccounts,
  } = useAccountSwitcherHook(pseudo, closeMenu);

  // Determine title and subtitle based on user type and context
  const { title, subtitle } = (() => {
    if (isAdminUser) {
      return isFacilityUser && facilityName
        ? {
            // Admin viewing a facility
            title: facilityName,
            subtitle: adminName ?? "Administrator",
          }
        : {
            // Admin not viewing a facility
            title: adminName ?? "Select Account",
            subtitle: "Administrator",
          };
    }

    if (isFacilityUser) {
      return facilityName
        ? {
            // Non-admin facility user
            title: facilityName,
            subtitle: userName,
          }
        : {
            // No facility selected (shouldn't happen for non-admin)
            title: userName ?? "Facility User",
            subtitle: undefined,
          };
    }

    // Fallback
    return {
      title: "Select Account",
      subtitle: undefined,
    };
  })();

  return (
    <Dropdown
      overlay={() => (
        <Box
          sx={(theme) => ({
            maxHeight: "60vh",
            overflowY: "auto",
            background: theme.palette.navbar?.gradient,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.4)",
            padding: 3,
          })}
        >
          {isAdminUser ? (
            <AccountSwitcherDropdownAdmin
              adminUserId={adminUserId}
              agentDetails={agentDetails}
              workplaceDetails={workplaceDetails}
              handleChange={handleChange}
              searchAccounts={searchAccounts}
              setAccountsListVisible={setAccountsListVisible}
            />
          ) : (
            <AccountSwitcherMenuWorkplace
              workplaceDetails={workplaceDetails}
              handleChange={handleChange}
            />
          )}
        </Box>
      )}
      visible={accountsListVisible}
      placement="bottomRight"
      onVisibleChange={handleOnDropdownVisible}
    >
      <Button variant="outlineInverted" sx={{ height: "100%" }}>
        <Stack direction="row" minWidth="400px" alignItems="center" gap={3}>
          <Avatar
            name={title}
            sx={(theme) => ({
              bgcolor: theme.palette.navbar?.avatarBackground,
              color: theme.palette.navbar?.avatarText,
              flexShrink: 0,
            })}
          />
          <Stack direction="column" flex={1} minWidth={0}>
            <Text
              variant="body1"
              textAlign="left"
              color={(theme) => theme.palette.navbar?.userName}
            >
              {title}
            </Text>
            {subtitle && (
              <Text variant="caption" textAlign="left">
                {subtitle}
              </Text>
            )}
          </Stack>
          <Icon
            type="caret-bottom"
            sx={(theme) => ({
              color: theme.palette.button?.outline.inverted.hover.color,
              flexShrink: 0,
            })}
          />
        </Stack>
      </Button>
    </Dropdown>
  );
}
